import { useMemo, useState } from 'react';
import { Download, Search, X } from 'lucide-react';
import type { ChartDataset } from 'chart.js';
import { ChartBox } from '../components/ChartBox';
import { KpiCard } from '../components/KpiCard';
import { Tag } from '../components/Tag';
import type { OpdEventRow, OpdResponse, Tone } from '../types';
import { exportCsv } from '../utils';

const TYPE_LABEL: Record<string, string> = {
  stock_inquiry: 'Hỏi hàng',
  order_issue: 'Issue đơn',
  category_announcement: 'Thông báo ngành hàng',
};
const TYPE_COLOR: Record<string, string> = {
  stock_inquiry: '#2563eb',
  order_issue: '#d71920',
  category_announcement: '#7c3aed',
};
const TYPE_TONE: Record<string, Tone> = {
  stock_inquiry: 'blue',
  order_issue: 'red',
  category_announcement: 'violet',
};
const REGION_LABEL: Record<string, string> = { HCM: 'TP.HCM', HN: 'Hà Nội', ALL: 'Toàn quốc' };
const RANGE_OPTIONS = [
  { value: '7d', label: '7 ngày' },
  { value: '30d', label: '30 ngày' },
  { value: '90d', label: '90 ngày' },
  { value: 'all', label: 'Tất cả' },
];

type FilterDimension = 'eventType' | 'region' | 'priority' | 'qc' | 'group';
type Filters = Record<FilterDimension, string[]>;
const EMPTY_FILTERS: Filters = { eventType: [], region: [], priority: [], qc: [], group: [] };

const num = (value: number) => value.toLocaleString('vi-VN');
const typeLabel = (value: string) => TYPE_LABEL[value] || value || 'Chưa phân loại';
const regionLabel = (value: string) => value === '__unknown__' ? 'Chưa rõ' : REGION_LABEL[value] || value || 'Chưa rõ';
const priorityLabel = (value: string) => value === '__unknown__' ? 'Chưa gắn' : ({ high: 'Cao', urgent: 'Khẩn', medium: 'Trung bình', low: 'Thấp' }[value] || value || 'Chưa gắn');
const qcLabel = (value: string) => value === '__unknown__' ? 'Chưa QC' : ({ passed: 'Đạt', partial: 'Thiếu một phần', rejected: 'Không đạt' }[value] || value || 'Chưa QC');
const safeList = (value?: string[] | null) => value?.filter(Boolean) || [];
const eventRegions = (row: OpdEventRow) => safeList(row.region_codes).length ? safeList(row.region_codes) : ['__unknown__'];
const eventStores = (row: OpdEventRow) => safeList(row.store_codes);

function productNames(row: OpdEventRow): string[] {
  const values = [row.product_name, row.product_code];
  for (const product of row.products || []) values.push(product.product_name, product.product_code);
  return [...new Set(values.filter((value): value is string => Boolean(value?.trim())).map(value => value.trim()))];
}

function rankProductNames(row: OpdEventRow): string[] {
  const values = [row.product_name || row.product_code];
  for (const product of row.products || []) values.push(product.product_name || product.product_code);
  return [...new Set(values.filter((value): value is string => Boolean(value?.trim())).map(value => value.trim()))];
}

function eventSummary(row: OpdEventRow): string {
  return row.inquiry_need || row.issue_summary || row.announcement_title || row.announcement_body || row.notes || row.raw_text || 'Chưa có tóm tắt';
}

function localDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('vi-VN', { dateStyle: 'short', timeStyle: 'short' });
}

function dimensionValues(row: OpdEventRow, dimension: FilterDimension): string[] {
  if (dimension === 'eventType') return [row.event_type || '__unknown__'];
  if (dimension === 'region') return eventRegions(row);
  if (dimension === 'priority') return [row.priority || '__unknown__'];
  if (dimension === 'qc') return [row.qc_status || '__unknown__'];
  return [row.source_group_name || '__unknown__'];
}

function searchableText(row: OpdEventRow): string {
  return [row.capture_id, row.order_code, row.product_code, row.product_name, row.brand, row.category_name,
    row.inquiry_need, row.issue_summary, row.announcement_title, row.announcement_body, row.notes, row.raw_text,
    row.sender_name, row.source_group_name, ...productNames(row), ...eventRegions(row), ...eventStores(row)]
    .filter(Boolean).join(' ').toLocaleLowerCase('vi-VN');
}

function matchesFilters(row: OpdEventRow, filters: Filters, query: string, exclude?: FilterDimension): boolean {
  if (query && !searchableText(row).includes(query.toLocaleLowerCase('vi-VN'))) return false;
  return (Object.keys(filters) as FilterDimension[]).every(dimension => {
    if (dimension === exclude || filters[dimension].length === 0) return true;
    const values = dimensionValues(row, dimension);
    return filters[dimension].some(value => values.includes(value));
  });
}

function countDimension(rows: OpdEventRow[], dimension: FilterDimension): Array<[string, number]> {
  const counts = new Map<string, number>();
  rows.forEach(row => {
    [...new Set(dimensionValues(row, dimension))].forEach(value => counts.set(value, (counts.get(value) || 0) + 1));
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1]);
}

function barDataset(entries: Array<[string, number]>, active: string[], color: string): ChartDataset {
  return {
    label: 'Số topic',
    data: entries.map(([, count]) => count),
    backgroundColor: entries.map(([key]) => active.length === 0 || active.includes(key) ? color : `${color}45`),
    borderRadius: 5,
    maxBarThickness: 44,
  } as unknown as ChartDataset;
}

export function OpdScreen({ data, loaded, busy, range, onRange }: {
  data: OpdResponse | null;
  loaded: boolean;
  busy: boolean;
  range: string;
  onRange: (range: string) => void;
}) {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [query, setQuery] = useState('');
  const rows = data?.rows || [];

  const filtered = useMemo(() => rows.filter(row => matchesFilters(row, filters, query)), [rows, filters, query]);
  const contextRows = (dimension: FilterDimension) => rows.filter(row => matchesFilters(row, filters, query, dimension));
  const options = useMemo(() => ({
    eventType: countDimension(contextRows('eventType'), 'eventType'),
    region: countDimension(contextRows('region'), 'region'),
    priority: countDimension(contextRows('priority'), 'priority'),
    qc: countDimension(contextRows('qc'), 'qc'),
    group: countDimension(contextRows('group'), 'group'),
  }), [rows, filters, query]);

  const toggle = (dimension: FilterDimension, value: string) => setFilters(current => ({
    ...current,
    [dimension]: current[dimension].includes(value)
      ? current[dimension].filter(item => item !== value)
      : [...current[dimension], value],
  }));
  const clearAll = () => { setFilters(EMPTY_FILTERS); setQuery(''); };
  const activeCount = Object.values(filters).reduce((sum, values) => sum + values.length, 0) + (query ? 1 : 0);

  const kpis = useMemo(() => {
    const typeCount = (type: string) => filtered.filter(row => row.event_type === type).length;
    const discussed = filtered.filter(row => Number(row.message_count || 0) > 1).length;
    const attention = filtered.filter(row => ['high', 'urgent'].includes(row.priority || '') || !['passed', ''].includes(row.qc_status || '')).length;
    return { total: filtered.length, stock: typeCount('stock_inquiry'), issue: typeCount('order_issue'), announcement: typeCount('category_announcement'), discussed, attention };
  }, [filtered]);

  const trend = useMemo(() => {
    const map = new Map<string, { types: Record<string, number>; duration: number; durationCount: number }>();
    filtered.forEach(row => {
      const key = row.event_day || row.first_event_at?.slice(0, 10) || 'Chưa rõ';
      const item = map.get(key) || { types: {}, duration: 0, durationCount: 0 };
      item.types[row.event_type] = (item.types[row.event_type] || 0) + 1;
      if (Number.isFinite(Number(row.discussion_minutes))) {
        item.duration += Number(row.discussion_minutes || 0);
        item.durationCount += 1;
      }
      map.set(key, item);
    });
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([day, item]) => ({
      day,
      label: day === 'Chưa rõ' ? day : day.split('-').reverse().slice(0, 2).join('/'),
      ...item,
      avgDuration: item.durationCount ? Math.round(item.duration / item.durationCount) : 0,
    }));
  }, [filtered]);

  const typeEntries = useMemo(() => countDimension(filtered, 'eventType'), [filtered]);
  const regionEntries = useMemo(() => countDimension(filtered, 'region'), [filtered]);
  const productEntries = useMemo(() => {
    const map = new Map<string, { name: string; count: number; regions: Set<string>; issues: number; last: string }>();
    filtered.forEach(row => {
      const names = rankProductNames(row);
      (names.length ? names : ['Chưa xác định sản phẩm']).forEach(name => {
        const key = name.toLocaleLowerCase('vi-VN');
        const item = map.get(key) || { name, count: 0, regions: new Set<string>(), issues: 0, last: '' };
        item.count += 1;
        eventRegions(row).forEach(region => item.regions.add(region));
        if (row.event_type === 'order_issue') item.issues += 1;
        if ((row.last_event_at || '') > item.last) item.last = row.last_event_at || '';
        map.set(key, item);
      });
    });
    return [...map.values()].sort((a, b) => b.count - a.count || b.last.localeCompare(a.last)).slice(0, 12);
  }, [filtered]);

  const trendDatasets = useMemo(() => {
    const knownTypes = ['stock_inquiry', 'order_issue', 'category_announcement'];
    const presentTypes = [...new Set([...knownTypes, ...filtered.map(row => row.event_type)])].filter(type => filtered.some(row => row.event_type === type));
    const bars = presentTypes.map(type => ({
      label: typeLabel(type),
      data: trend.map(item => item.types[type] || 0),
      backgroundColor: TYPE_COLOR[type] || '#64748b',
      borderRadius: 4,
      stack: 'topics',
      type: 'bar',
      yAxisID: 'y',
    } as unknown as ChartDataset));
    return [...bars, {
      label: 'Thời lượng thảo luận TB (phút)',
      data: trend.map(item => item.avgDuration),
      borderColor: '#d97706',
      backgroundColor: '#d9770630',
      tension: 0.32,
      pointRadius: 3,
      type: 'line',
      yAxisID: 'y1',
    } as unknown as ChartDataset];
  }, [filtered, trend]);

  if (!loaded && busy) return <div className="empty">Đang đọc dữ liệu OPD live...</div>;
  if (!busy && loaded && rows.length === 0) return <div className="empty">Chưa có sự kiện OPD trong khoảng thời gian này.</div>;

  const exportRows = filtered.map(row => ({
    capture_id: row.capture_id,
    event_type: typeLabel(row.event_type),
    updated_at: row.last_event_at,
    regions: eventRegions(row).map(regionLabel).join(', '),
    stores: eventStores(row).join(', '),
    order_code: row.order_code,
    products: productNames(row).join(' | '),
    priority: priorityLabel(row.priority || ''),
    qc_status: qcLabel(row.qc_status || ''),
    message_count: row.message_count,
    discussion_minutes: row.discussion_minutes,
    summary: eventSummary(row),
    raw_text: row.raw_text,
  }));

  return (
    <>
      <section className="filters opd-filters">
        <div className="section-title">
          <div>
            <h2>Bộ lọc tác nghiệp OPD</h2>
            <span>Lọc chéo theo phân loại, khu vực, ưu tiên, QC và group nguồn</span>
          </div>
          <div className="seg" aria-label="Khoảng thời gian">
            {RANGE_OPTIONS.map(option => <button key={option.value} className={`tab ${range === option.value ? 'active' : ''}`} disabled={busy} onClick={() => onRange(option.value)} type="button">{option.label}</button>)}
          </div>
        </div>
        <div className="filter-grid opd-filter-grid">
          <div className="control">
            <label htmlFor="opd-search">Tìm kiếm</label>
            <div className="opd-search"><Search size={14} /><input id="opd-search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Mã đơn, SKU, nội dung..." /></div>
          </div>
          <FilterSelect label="Phân loại" entries={options.eventType} render={typeLabel} onPick={value => toggle('eventType', value)} />
          <FilterSelect label="Khu vực" entries={options.region} render={regionLabel} onPick={value => toggle('region', value)} />
          <FilterSelect label="Ưu tiên" entries={options.priority} render={priorityLabel} onPick={value => toggle('priority', value)} />
          <FilterSelect label="QC" entries={options.qc} render={qcLabel} onPick={value => toggle('qc', value)} />
          <FilterSelect label="Group nguồn" entries={options.group} render={value => value === '__unknown__' ? 'Chưa rõ' : value} onPick={value => toggle('group', value)} />
        </div>
        <div className="filter-pills">
          {query && <button className="pill filter" onClick={() => setQuery('')} type="button">Tìm: {query}<X size={12} /></button>}
          {(Object.keys(filters) as FilterDimension[]).flatMap(dimension => filters[dimension].map(value => (
            <button key={`${dimension}-${value}`} className="pill filter" onClick={() => toggle(dimension, value)} type="button">
              {filterLabel(dimension, value)}<X size={12} />
            </button>
          )))}
          {activeCount === 0 ? <span className="muted">Đang xem toàn bộ {num(rows.length)} topic trong kỳ.</span> : <button className="pill ghost" onClick={clearAll} type="button">Xoá {activeCount} điều kiện</button>}
        </div>
      </section>

      <section className="grid kpis">
        <KpiCard label="Tổng topic" value={num(kpis.total)} sub={`${num(rows.length)} topic trong dữ liệu kỳ`} tone="ink" />
        <KpiCard label="Hỏi hàng" value={num(kpis.stock)} sub="Nhu cầu load hàng / lịch về" tone="blue" />
        <KpiCard label="Issue đơn" value={num(kpis.issue)} sub="Phát sinh cần phối hợp xử lý" tone="red" />
        <KpiCard label="Thông báo" value={num(kpis.announcement)} sub="Thông tin từ ngành hàng" tone="violet" />
        <KpiCard label="Có thảo luận" value={`${kpis.discussed}/${kpis.total}`} sub="Topic ghi nhận từ 2 tin nhắn" tone="green" />
        <KpiCard label="Cần chú ý" value={num(kpis.attention)} sub="Ưu tiên cao hoặc QC chưa đạt" tone="amber" />
      </section>

      <section className="grid split space">
        <div className="card">
          <div className="section-title"><h2>Nhịp phát sinh & thời lượng thảo luận</h2><span>Stack theo loại · đường phút trung bình</span></div>
          <div className="chart tall"><ChartBox labels={trend.map(item => item.label)} datasets={trendDatasets} options={{
            scales: {
              x: { stacked: true, grid: { display: false } },
              y: { stacked: true, beginAtZero: true, title: { display: true, text: 'Số topic' } },
              y1: { beginAtZero: true, position: 'right', grid: { drawOnChartArea: false }, title: { display: true, text: 'Phút' } },
            },
          }} /></div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Cơ cấu loại trao đổi</h2><span>Click để lọc</span></div>
          <div className="chart tall"><ChartBox type="doughnut" labels={typeEntries.map(([type]) => typeLabel(type))} datasets={[{
            label: 'Số topic',
            data: typeEntries.map(([, count]) => count),
            backgroundColor: typeEntries.map(([type]) => TYPE_COLOR[type] || '#64748b'),
            borderWidth: 0,
          } as unknown as ChartDataset]} options={{ scales: {} }} onPointClick={({ index }) => typeEntries[index] && toggle('eventType', typeEntries[index][0])} /></div>
        </div>
      </section>

      <section className="grid split opd-summary-grid space atop">
        <div className="card">
          <div className="section-title"><h2>Phân bổ khu vực</h2><span>Click cột để lọc chéo</span></div>
          <div className="chart" style={{ height: Math.max(220, regionEntries.length * 42 + 70) }}><ChartBox labels={regionEntries.map(([region]) => regionLabel(region))} datasets={[barDataset(regionEntries, filters.region, '#0891b2')]} options={{ indexAxis: 'y', plugins: { legend: { display: false } } }} onPointClick={({ index }) => regionEntries[index] && toggle('region', regionEntries[index][0])} /></div>
        </div>
        <div className="card">
          <div className="section-title"><h2>Sản phẩm được hỏi nhiều</h2><span>Gộp theo tên / mã ghi nhận</span></div>
          <div className="scroll opd-rank-scroll"><table><thead><tr><th>Sản phẩm</th><th className="right">Topic</th><th>Khu vực</th><th>Cập nhật</th></tr></thead><tbody>
            {productEntries.map(item => <tr key={item.name}><td><b>{item.name}</b>{item.issues > 0 && <><br /><Tag tone="red">{item.issues} issue</Tag></>}</td><td className="right tnum"><b>{num(item.count)}</b></td><td>{[...item.regions].map(regionLabel).join(', ')}</td><td className="muted">{localDateTime(item.last)}</td></tr>)}
            {productEntries.length === 0 && <tr><td colSpan={4} className="empty">Chưa có sản phẩm trong tập lọc.</td></tr>}
          </tbody></table></div>
        </div>
      </section>

      <section className="card space">
        <div className="section-title">
          <div><h2>Danh sách topic tác nghiệp</h2><span>{num(filtered.length)} kết quả · newest first · giữ đủ thread/reply trong raw record</span></div>
          <button className="btn primary" disabled={filtered.length === 0} onClick={() => exportCsv(exportRows, undefined, 'OPD_Hoi_hang_Issue')} type="button"><Download size={15} />Export CSV</button>
        </div>
        {data?.meta?.truncated && <div className="opd-warning">API đã trả {num(data.meta.returned || 0)}/{num(data.meta.total || 0)} topic. Thu hẹp khoảng thời gian để xem đủ dữ liệu.</div>}
        <div className="scroll tall"><table className="opd-table"><thead><tr><th>Cập nhật</th><th>Loại</th><th>Khu vực</th><th>Sản phẩm / đơn</th><th>Nội dung ghi nhận</th><th>Trao đổi</th><th>QC</th></tr></thead><tbody>
          {filtered.map(row => <tr key={row.capture_id}>
            <td className="tnum"><b>{localDateTime(row.last_event_at)}</b><small className="opd-cell-meta">{row.sender_name || 'Chưa rõ người gửi'}<br />{row.capture_id}</small></td>
            <td><Tag tone={TYPE_TONE[row.event_type] || 'gray'}>{typeLabel(row.event_type)}</Tag>{['high', 'urgent'].includes(row.priority || '') && <><br /><Tag tone="amber">{priorityLabel(row.priority || '')}</Tag></>}</td>
            <td>{eventRegions(row).map(regionLabel).join(', ')}{eventStores(row).length > 0 && <small className="opd-cell-meta">Store: {eventStores(row).join(', ')}</small>}</td>
            <td><b>{productNames(row).join(' · ') || '—'}</b>{row.order_code && <small className="opd-cell-meta">Đơn: {row.order_code}</small>}{row.brand && <small className="opd-cell-meta">{row.brand}{row.category_name ? ` · ${row.category_name}` : ''}</small>}</td>
            <td className="opd-topic"><div className="opd-summary">{eventSummary(row)}</div>{row.raw_text && <details className="opd-raw"><summary>Xem trao đổi gốc</summary><pre>{row.raw_text}</pre></details>}<small className="opd-cell-meta">{row.source_group_name || 'Chưa rõ group'} · {row.discussion_key || 'không có discussion key'}</small></td>
            <td className="tnum"><b>{num(Number(row.message_count || 1))} tin</b><small className="opd-cell-meta">{num(Number(row.discussion_minutes || 0))} phút</small></td>
            <td><Tag tone={row.qc_status === 'passed' ? 'green' : row.qc_status === 'partial' ? 'amber' : 'gray'}>{qcLabel(row.qc_status || '')}</Tag></td>
          </tr>)}
          {filtered.length === 0 && <tr><td colSpan={7} className="empty">Không có topic khớp bộ lọc hiện tại.</td></tr>}
        </tbody></table></div>
      </section>
      <div className="opd-source">Nguồn live: {data?.meta?.source || 'public.opd_operational_events'} · cập nhật {localDateTime(data?.meta?.refreshed_at)}</div>
    </>
  );
}

function FilterSelect({ label, entries, render, onPick }: {
  label: string;
  entries: Array<[string, number]>;
  render: (value: string) => string;
  onPick: (value: string) => void;
}) {
  return <div className="control"><label>{label}</label><select value="" onChange={event => { if (event.target.value) onPick(event.target.value); }}><option value="">Tất cả ({entries.reduce((sum, [, count]) => sum + count, 0)})</option>{entries.map(([value, count]) => <option key={value} value={value}>{render(value)} ({count})</option>)}</select></div>;
}

function filterLabel(dimension: FilterDimension, value: string): string {
  if (dimension === 'eventType') return `Loại: ${typeLabel(value)}`;
  if (dimension === 'region') return `Khu vực: ${regionLabel(value)}`;
  if (dimension === 'priority') return `Ưu tiên: ${priorityLabel(value)}`;
  if (dimension === 'qc') return `QC: ${qcLabel(value)}`;
  return `Group: ${value === '__unknown__' ? 'Chưa rõ' : value}`;
}


